"use client";

/**
 * Drawn where the reader is: in the modal `<dialog>` on top, or where it
 * stands when none is open (Issue 705).
 *
 * A modal dialog — a card over the page (shell/Modal), the round and key
 * dialogs, onboarding's editors — is in the browser's top layer, which no
 * `z-index` outbids, and while it is open everything outside it is inert:
 * not hit by a press, not focused, not there to a screen reader. So the
 * shell's wait and its failure notice, drawn on the page, sat under an open
 * card: a wait nobody could see, and a notice whose × nobody could press.
 * What a surface of the shell's own has to say while a dialog is open, it
 * says inside that dialog; `position: fixed` in one still measures the
 * viewport (the dialogs here set no transform or filter), so a sheet drawn
 * there covers the screen as it does from the page, backdrop and all.
 *
 * Not the top layer of its own, which is where it would seem to belong: a
 * popover opened over a modal dialog is drawn above it and is inert all the
 * same (measured in Chrome 154: presses go through it, the × is never
 * pressed), and a modal dialog of its own would lock the whole page for as
 * long as the notice is up when no card is open. Nor a host each dialog
 * registers: the platform already says which dialogs are modal (`:modal`),
 * and one written later that forgot to register would put this back.
 *
 * Where this stops: dialogs are the only top layer this app opens. A popover
 * or a fullscreen element would be drawn over the page's layer as well, and
 * would need its own answer here before either is used.
 *
 * Any surface of the shell's that must be seen, or pressed, over whatever
 * is open is drawn through this — not given a higher `z-index`.
 */
import { useSyncExternalStore, type ReactNode } from "react";
import { createPortal } from "react-dom";

// When each dialog was last opened. The top layer is ordered by that, and
// the page cannot ask for the order: the one on top is the newest open one.
let opened = 0;
const openedAt = new WeakMap<Element, number>();
// By tag name, and the observer below from the document's own window: both
// are realm-independent, where a global constructor is another window's in
// an embedded document or a DOM test harness (as `formTarget` in
// NavigationPending).
function stamp(event: Event) {
  const target = event.target as Element | null;
  if (target?.tagName === "DIALOG" && (event as ToggleEvent).newState === "open") openedAt.set(target, ++opened);
}
// From the first import, not from the first wait: the dialog a wait is drawn
// over was opened before it. (Fast Refresh runs this again in development
// and adds another, stamping a map nobody reads any more: harmless.)
//
// A dialog's `toggle` is recent (Chrome 132, Firefox 133). Where there is
// none, nothing is stamped and the order falls back to the document's, which
// is the order dialogs are opened in whenever one is opened from inside the
// one on top.
if (typeof document !== "undefined") document.addEventListener("toggle", stamp, true);

/**
 * The modal dialog on top, or null. The newest opened; one never seen
 * opening counts as oldest, and among equals the later in the document wins
 * — a modal opened from inside another is inside it, since nothing outside
 * the one on top can be pressed.
 */
export function topModal(doc: Document): HTMLDialogElement | null {
  let top: HTMLDialogElement | null = null;
  let at = -1;
  for (const dialog of doc.querySelectorAll("dialog")) {
    if (!dialog.matches(":modal")) continue;
    const n = openedAt.get(dialog) ?? 0;
    if (n >= at) {
      top = dialog;
      at = n;
    }
  }
  return top;
}

// Watched only while something is drawn through `OnTop`. A dialog shutting
// or opening says so (`toggle`), and shutting says so again in every engine
// (`close`) — a dialog shut but still mounted changes no node, so without it
// the sheet would stay in a dialog that is no longer shown. One taken out of
// the document while open — a component unmounted without closing it —
// leaves the top layer saying nothing, and only the document's own
// mutations show it went.
function subscribe(changed: () => void): () => void {
  document.addEventListener("toggle", changed, true);
  document.addEventListener("close", changed, true);
  const observer = new document.defaultView!.MutationObserver(changed);
  observer.observe(document.documentElement, { childList: true, subtree: true });
  return () => {
    document.removeEventListener("toggle", changed, true);
    document.removeEventListener("close", changed, true);
    observer.disconnect();
  };
}

const onPage = () => null;
const current = () => topModal(document);

export function OnTop({ children }: { children: ReactNode }) {
  const host = useSyncExternalStore(subscribe, current, onPage);
  return host ? createPortal(children, host) : children;
}
